import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getFinanceElfLoansList } from '../../endpoints/financeElfLoans';
import { ElfLoanListItem } from '../../types/financeElfLoans';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import { SortOrder } from '../../types/table';

const formatStatuses = (value: string): string => {
  const parts = (value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return '—';
  return parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(', ');
};

const attributeChips = (row: ElfLoanListItem) => {
  const chips: string[] = [];
  if (row.user_bankruptcy) chips.push('Bankruptcy');
  if (row.principal_only) chips.push('Principal only');
  if (row.written_off) chips.push('Written off');
  if (row.sent_to_collections) chips.push('Collections');
  return chips;
};

const ElfLoansList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof ElfLoanListItem>('borrower_name');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [delaySearch, setDelaySearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [status, setStatus] = useState<string>(
    searchParams.get('status') || ''
  );
  const [bankruptcy, setBankruptcy] = useState(
    searchParams.get('user_bankruptcy') === '1'
  );
  const [principalOnly, setPrincipalOnly] = useState(
    searchParams.get('principal_only') === '1'
  );
  const [writtenOff, setWrittenOff] = useState(
    searchParams.get('written_off') === '1'
  );
  const [sentToCollections, setSentToCollections] = useState(
    searchParams.get('sent_to_collections') === '1'
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const apply = (key: string, value: string) => {
      if (!value) next.delete(key);
      else next.set(key, value);
    };
    apply('search', search);
    apply('status', status);
    apply('user_bankruptcy', bankruptcy ? '1' : '');
    apply('principal_only', principalOnly ? '1' : '');
    apply('written_off', writtenOff ? '1' : '');
    apply('sent_to_collections', sentToCollections ? '1' : '');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next);
    }
  }, [
    search,
    status,
    bankruptcy,
    principalOnly,
    writtenOff,
    sentToCollections,
    searchParams,
    setSearchParams,
  ]);

  const params = {
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    order_by: String(orderBy),
    order: order || undefined,
    status: status || undefined,
    user_bankruptcy: bankruptcy || undefined,
    principal_only: principalOnly || undefined,
    written_off: writtenOff || undefined,
    sent_to_collections: sentToCollections || undefined,
  };

  const { data, isLoading } = useApiGet(
    [
      'finance-elf-loans',
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      status,
      bankruptcy,
      principalOnly,
      writtenOff,
      sentToCollections,
    ],
    () => getFinanceElfLoansList(params),
    {
      enabled: true,
      retry: 1,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      setPage(0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: keyof ElfLoanListItem) => {
    if (
      property !== 'borrower_name' &&
      property !== 'borrower_email' &&
      property !== 'imis_id' &&
      property !== 'statuses'
    ) {
      return;
    }
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const columns: ServerSideTableColumn<ElfLoanListItem>[] = [
    {
      id: 'borrower_name',
      label: 'Name',
      width: '22%',
      firstCell: true,
      render: (_value, row) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 320,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.35,
            fontWeight: 500,
          }}
        >
          {row.borrower_name?.trim() || '—'}
        </Typography>
      ),
    },
    {
      id: 'borrower_email',
      label: 'Email',
      width: '24%',
      render: (_value, row) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ wordBreak: 'break-all' }}
        >
          {row.borrower_email?.trim() || '—'}
        </Typography>
      ),
    },
    {
      id: 'imis_id',
      label: 'iMIS ID',
      width: '12%',
      render: (_value, row) => (
        <Typography variant="body2">{row.imis_id?.trim() || '—'}</Typography>
      ),
    },
    {
      id: 'statuses',
      label: 'Status',
      width: '14%',
      render: (_value, row) => (
        <Typography variant="body2">{formatStatuses(row.statuses)}</Typography>
      ),
    },
    {
      id: 'user_bankruptcy',
      label: 'Attributes',
      sortable: false,
      render: (_value, row) => {
        const chips = attributeChips(row);
        if (chips.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {chips.map((label) => (
              <Chip key={label} size="small" label={label} variant="outlined" />
            ))}
          </Box>
        );
      },
    },
  ];

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1}>
        <Typography variant="h4" color="primary">
          ELF Loans
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ py: 2, maxWidth: 480 }}>
          <TextField
            label="Search by name, email, iMIS, or phone"
            id="elf-loans-search-field"
            size="small"
            value={delaySearch}
            fullWidth
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            pb: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="elf-loans-status-label">Status</InputLabel>
            <Select
              labelId="elf-loans-status-label"
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={bankruptcy}
                onChange={(event) => {
                  setBankruptcy(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Bankruptcy"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={principalOnly}
                onChange={(event) => {
                  setPrincipalOnly(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Principal only"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={writtenOff}
                onChange={(event) => {
                  setWrittenOff(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Written off"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={sentToCollections}
                onChange={(event) => {
                  setSentToCollections(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Sent to collections"
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={(row) => navigate(`/finance/elf-loans/${row.id}`)}
          emptyMessage="No ELF loans found."
        />
      </Grid>
    </Grid>
  );
};

export default ElfLoansList;
